import {
  Box,
  Chip,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from '@mui/material';
import { TableHeader } from '../../components/formlib/TableHeader';
import { useEffect, useState, FC, useContext } from 'react';
import { ActivitySheetHeaderType } from './types';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import { getHeaderValues } from './activityUtils';
import {
  Activity,
  ActivitySheet,
  updateAppointment,
} from '../../services/configApi/activitySheet/weeklyActivitySheetProvider';
import { getTimeString } from '../../utils/dateUtil';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ResponseType } from '../../utils/type';
import { ReactComponent as UndoIcon } from '../../assets/images/undoIcon.svg';
import { ReactComponent as EditIcon } from '../../assets/images/editvector.svg';
import { ReactComponent as MessageIcon } from '../../assets/images/messageIcon.svg';
import { ReactComponent as ArchiveIcon } from '../../assets/images/archiveIcon.svg';
import { ReactComponent as StatusIcon } from '../../assets/images/statusIcon.svg';
import { ReactComponent as HistoryIcon } from '../../assets/images/history-icon.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/infoIcon.svg';
import HoursOutPopover from './HoursOutPopover';
import ActionToolTip from '../../components/formlib/ActionToolTip';
import TextWithIcon from '../../components/formlib/TextWithIcon';
import { StatusType, UpdateTime } from './ActivityTableRow';
import ActivityEditModal from '../../components/formlib/modal/ActivityEditModal';
import ActivityNoteModal from '../../components/formlib/modal/ActivityNoteModal';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import {
  ApiError,
  ApiMessage,
  isCustomError,
} from '../../services/ApiResponseHandler';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { ActivitySheetContext } from '../../context/ActivitySheetContext';
import {
  formatNumberToDecimal,
  formatNumberToDecimalString,
  getStatusColor,
} from '../../utils/utilities';
import dayjs from 'dayjs';
import PayrollHistoryDialog, { PayrollHistory } from './PayrollHistoryDialog';
import HoursOutHostoryPopup, {
  HoursOutHistoryProps,
} from './HoursOutHostoryPopup';
import ModalPopup from '../../components/formlib/ModalPopup';

interface PayrollHoursViewProps {
  selectedDate: Date;
  activityDetails?: ActivitySheet;
  employeeName: string;
  handleNoteUpdate: (activitySheetId: string, note: string) => void;
}

const PayrollHoursView: FC<PayrollHoursViewProps> = ({
  selectedDate,
  activityDetails,
  employeeName,
  handleNoteUpdate,
}) => {
  const [headerCells, setHeaderCells] = useState<ActivitySheetHeaderType>([]);
  const [successOrError, setSuccessOrError] = useState<ResponseType>('success');
  const [toastrId, setToastrId] = useState<string>();
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const { context, canPayrollHoursEdit } = useContext(ActivitySheetContext);
  const [historyDialog, setHistoryDialog] = useState<PayrollHistory | null>(
    null,
  );
  const [hoursOutHistoryDialog, setHoursOutHistoryDialog] =
    useState<HoursOutHistoryProps | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const [showConfirmationPopup, setShowConfirmationPopup] =
    useState<boolean>(false);

  const [state, setState] = useState<{
    openEditModel: boolean;
    openNoteModel: boolean;
    openGroupBillingModel: boolean;
    openVisitModal: boolean;
    visitId: string | null;
    visitDescription: string | null;
    progressNotesId: string | null;
    activity: Activity | null;
  }>({
    openEditModel: false,
    openNoteModel: false,
    openGroupBillingModel: false,
    openVisitModal: false,
    visitId: null,
    visitDescription: null,
    progressNotesId: null,
    activity: null,
  });

  useEffect(() => {
    if (activityDetails?.employeeType) {
      const cells = getHeaderValues(activityDetails?.employeeType);
      if (
        checkPermissionForFeature(
          'backend.payroll_hours_ras',
          'viewPermission',
        ) ||
        canPayrollHoursEdit
      ) {
        cells.push(
          {
            id: 'status',
            labelId: 'Dashboard.Status',
            defaultLabelId: 'Status',
            numeric: false,
            requiredSorting: false,
          },
          {
            id: 'actions',
            labelId: 'actions',
            defaultLabelId: 'Actions',
            numeric: false,
            requiredSorting: false,
          },
        );
      }
      setHeaderCells(cells);
    }
  }, [activityDetails?.employeeType]);

  const getAppStartTime = (
    time: string | null,
    format: string,
    utc: boolean,
  ) => {
    if (time === null) {
      return '';
    }
    return getTimeString(time, format, utc);
  };

  const getBackgroundColor = (activity: Activity) => {
    if (dayjs(activity.day).day() === 0 || dayjs(activity.day).day() === 6) {
      return 'var(--thom-green-primary2-bg)';
    }
    if (activity.isHoliday) {
      return '#FFEBDD';
    } else if (activity.archived) {
      return '#97A6A5';
    } else if (activity.rowId === 0) {
      return '#ffffff';
    } else {
      return '#ECF9F8';
    }
  };

  const hasPendingAction = () => {
    const hasSubmitted = activityDetails?.activities?.find(
      (activity) => activity.payrollStatus === 'Submitted',
    );
    if (hasSubmitted) {
      return true;
    }
    return false;
  };

  const displayPopup = (activity: Activity) => {
    return activity.hoursOutDec !== null && activity.hoursOutDec !== 0;
  };

  const updateNoteState = (activity: Activity | null) => {
    setState((previousState) => ({
      ...previousState,
      openNoteModel: !previousState.openNoteModel,
      activity: activity,
    }));
  };

  const updateEditState = (activity: Activity | null, openModal: boolean) => {
    setState((previousState) => ({
      ...previousState,
      activity: activity,
      openEditModel: openModal,
    }));
  };

  const updateTimeOrNote = (
    activitySheetId: string,
    data: UpdateTime,
    updateType: 'NOTE' | 'TIME',
  ) => {
    toggleLoader(true);
    updateAppointment(
      dayjs(selectedDate).format('MM/DD/YYYY'),
      {
        activitySheetId: activitySheetId,
        description: data.note,
        approvedNonBillableHoursDec: data.approvedNonBillableHoursDec,
        otherAssocWorkHrsDec: data.otherAssocWorkHrsDec,
        otherNonBillableHrsDec: data.otherNonBillableHrsDec,
        totalHoursDec: data.totalHoursDec,
        lunchTimeIncluded: data.lunchTimeIncluded,
        noShowTravelDocDec: data.noShowTravelDocDec,
        timeOffList: data.timeOffList,
        activityDate: data.activityDate,
        employeeType: activityDetails?.employeeType || null,
      },
      true,
      activityDetails?.employeeId ?? '',
    )
      .then(() => {
        updateEditState(null, false);
        setSuccessOrError('success');
        if (updateType === 'NOTE') {
          if (data.note !== undefined) {
            handleNoteUpdate(activitySheetId, data.note);
          }
          setToastrId('noteUpdateSuccessMessage');
          setToastrDefaultMessage('Note updated successfully');
        } else {
          setToastrId('dataUpdatedTextMessage');
          setToastrDefaultMessage('Data Updated Successfully');
          setTimeout(() => {
            context?.reload?.();
          }, 1000);
        }
        toggleLoader(false);
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          const errorResponse = error as ApiMessage;
          if (errorResponse.code === 1009 || errorResponse.code === 409) {
            setToastrId('TimeOffConflict');
            setToastrDefaultMessage(
              'Your Time off overlaps with an existing request. Adjust the dates or time to avoid conflict.',
            );
          } else if (errorResponse.code === 1015) {
            setToastrId('TimeOffConflictForHolidays');
            setToastrDefaultMessage(
              'No Time off is required for the chosen days or time.',
            );
          } else if (errorResponse.code === 1018) {
            setToastrId('myTimeOffExceedingMsg');
            setToastrDefaultMessage(
              `You can't submit a leave request exceeding your available balance.`,
            );
          } else if (errorResponse.code === 1029) {
            setToastrId('myTimeOffInvalidDates');
            setToastrDefaultMessage(
              `The dates selected are prior to the start date of the program or after the end date. Please choose different dates.`,
            );
          } else if (errorResponse.code === 1030) {
            setToastrId('myTimeOffActivitysheetError');
            setToastrDefaultMessage(
              `  Activity sheet has already been Approved for the selected dates. Choose different dates.`,
            );
          } else if (errorResponse.code === 1165) {
            setToastrId('myTimeDurationError');
            setToastrDefaultMessage(` Duration must be greater than zero.`);
          } else if (errorResponse.code === 1166) {
            setToastrId('myTimeOffStartDurationError');
            setToastrDefaultMessage(
              ` Start date and duration are required for hourly time-off.`,
            );
          } else if (errorResponse.code === 1167) {
            setToastrId('myTimeOffDurationFormatError');
            setToastrDefaultMessage(` Duration entered is not a valid number.`);
          } else if (errorResponse.code === 1168) {
            setToastrId('myTimeOffMultipleDatesError');
            setToastrDefaultMessage(
              `  The time-off cannot be applied  for Multiple dates.`,
            );
          } else if (errorResponse.code === 1183) {
            setToastrId('myTimeOffApplyError');
            setToastrDefaultMessage(
              `You cannot apply more than 40 hours of Sick/LTI in a given Fiscal Year."
`,
            );
          } else if (updateType === 'NOTE') {
            setToastrId('noteUpdateFailureMessage');
            setToastrDefaultMessage('Failed to update note');
          } else {
            setToastrId('myTimeOffFailedToUpdate');
            setToastrDefaultMessage('Failed to update TimeOff');
          }
        }
      });
  };

  const canHideEditUpdateButton = (payrollStatus: StatusType | null) => {
    return !(
      payrollStatus === null ||
      payrollStatus === 'Not Submitted' ||
      !canPayrollHoursEdit
    );
  };

  return (
    <>
      {toastrId && (
        <SnackBarComponent
          open={toastrId !== undefined}
          handleClose={() => {
            setToastrId(undefined);
          }}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
          successOrError={successOrError}
        />
      )}
      {showConfirmationPopup && (
        <ModalPopup
          open={showConfirmationPopup}
          description="payrollHoursSubmissionWarningMessage"
          onCancel={() => setShowConfirmationPopup(false)}
          onOk={() => {
            context?.submitPayrollActivities?.();
            setShowConfirmationPopup(false);
          }}
          labelId1="Clientpage.cancelbtn"
          negativeActionLabel="Cancel"
          labelId2="Clientpage.Okbtn"
          positiveActionLabel="Ok"
        />
      )}
      {hoursOutHistoryDialog?.open && (
        <HoursOutHostoryPopup
          open={hoursOutHistoryDialog.open}
          employeeId={hoursOutHistoryDialog.employeeId}
          date={hoursOutHistoryDialog.date}
          handleClose={() =>
            setHoursOutHistoryDialog({
              open: false,
              employeeId: '',
              date: '',
            })
          }
          titleId={'timeOffHistoryText'}
          defaultMessage={'Time Off History'}
        />
      )}
      {historyDialog?.open && (
        <PayrollHistoryDialog
          open={historyDialog.open}
          date={historyDialog.date}
          field={historyDialog.field}
          employeeId={historyDialog.employeeId}
          titleId={historyDialog.titleId}
          defaultMessage={historyDialog.defaultMessage}
          handleClose={() =>
            setHistoryDialog({
              open: false,
              date: '',
              field: '',
              employeeId: '',
              titleId: '',
              defaultMessage: '',
            })
          }
        />
      )}
      {state.openEditModel && state.activity && (
        <ActivityEditModal
          employeeType={activityDetails?.employeeType ?? null}
          employeeId={activityDetails?.employeeId ?? null}
          employeeName={employeeName}
          open={state.openEditModel}
          canEdit={canHideEditUpdateButton(state.activity.payrollStatus)}
          selectedDate={state.activity.day ?? ''}
          totalHoursDec={state.activity.totalHoursDec}
          lunchTimeIncluded={state.activity.lunchTimeIncluded}
          approvedNonBillableHoursDec={
            state.activity.approvedNonBillableHoursDec
          }
          otherAssocWorkHrsDec={state.activity.otherAssocWorkHrsDec}
          otherNonBillableHrsDec={state.activity.otherNonBillableHrsDec}
          noShowTravelDocDec={state.activity.noShowTravelDocDec}
          handleClose={() => updateEditState(null, false)}
          onTimeEdit={(values) => {
            if (updateTimeOrNote && state.activity) {
              updateTimeOrNote(
                state.activity.activitySheetId,
                {
                  totalHoursDec: values.totalHoursDec,
                  lunchTimeIncluded: values.lunchTimeIncluded,
                  approvedNonBillableHoursDec:
                    values.approvedNonBillableHoursDec,
                  otherAssocWorkHrsDec: values.otherAssocWorkHrsDec,
                  otherNonBillableHrsDec: values.otherNonBillableHrsDec,
                  noShowTravelDocDec: values.noShowTravelDocDec,
                  note: state.activity.description ?? '',
                  timeOffList: values.timeOffList,
                  activityDate: dayjs
                    .utc(values.activityDate)
                    .startOf('day')
                    .format('MM/DD/YYYY'),
                },
                'TIME',
              );
            }
          }}
        />
      )}
      {state.openNoteModel && state.activity && (
        <ActivityNoteModal
          open={state.openNoteModel}
          note={state.activity.description ?? ''}
          handleClose={() => updateNoteState(null)}
          maxLength={2000}
          updateNote={(note: string) => {
            if (state.activity) {
              updateNoteState(null);
              updateTimeOrNote(
                state.activity.activitySheetId,
                {
                  totalHoursDec: state.activity.totalHoursDec,
                  approvedNonBillableHoursDec:
                    state.activity.approvedNonBillableHoursDec,
                  note: note.trim(),
                  activityDate: dayjs
                    .utc(state.activity.day)
                    .startOf('day')
                    .format('MM/DD/YYYY'),
                },
                'NOTE',
              );
            }
          }}
        />
      )}
      <TableContainer className="tableStyles">
        <Table>
          <TableHeader
            className="listDataTableHead"
            labelSxProp={{ whiteSpace: 'normal' }}
            headerNames={headerCells}
            checkBoxRequired={false}
          />
          <TableBody className="tableRowcss">
            {activityDetails?.activities?.map((activity, index) => (
              <TableRow
                key={activity.activitySheetId}
                sx={{
                  backgroundColor: getBackgroundColor(activity),
                  opacity: activity.archived ? '0.7' : '1',
                }}
              >
                <TableCell className="border-bottom-0">
                  <Box>
                    {activity.isHoliday !== null && (
                      <Box>
                        <Tooltip title={activity.holidayTitle}>
                          <Chip
                            size="small"
                            label={activity.holidayTitle}
                            sx={{
                              position: 'relative',
                              fontFamily: 'Lato-Regular',
                              fontSize: '10px',
                              color: 'white',
                              backgroundColor: '#F96300',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '150px',
                            }}
                          />
                        </Tooltip>
                      </Box>
                    )}
                    <MediumTypography
                      sxProps={{
                        color:
                          activity.isHoliday !== null && activity.isHoliday
                            ? '#F96300'
                            : '#2A4241',
                      }}
                      className="singleLine"
                      label={getAppStartTime(
                        activity.day,
                        'dddd, MMM DD',
                        true,
                      )}
                    />
                  </Box>
                </TableCell>

                <TableCell
                  className="border-bottom-0 paddingZero"
                  sx={{
                    backgroundColor:
                      activity.isHoliday !== null && activity.isHoliday
                        ? '#FFEBDD'
                        : 'var(--thom-mono-gray6)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <MediumTypography
                      className="pl-md pr-sm"
                      label={
                        activity.billableHoursDec === null
                          ? '-'
                          : formatNumberToDecimal(activity.billableHoursDec)
                      }
                    />
                    {activity.payrollHistoryFields?.includes(
                      'billable_hours',
                    ) && (
                      <HistoryIcon
                        className="enablePopupIcon"
                        onClick={() =>
                          setHistoryDialog({
                            open: true,
                            date: getAppStartTime(
                              activity.day,
                              'MM/DD/YYYY',
                              true,
                            ),
                            field: 'billable_hours',
                            employeeId: activityDetails?.employeeId ?? '',
                            titleId: 'billableHrsText',
                            defaultMessage: 'Billable Hrs',
                          })
                        }
                      />
                    )}
                  </Box>
                </TableCell>

                {activityDetails?.employeeType !== 'Salaried Exempt' && (
                  <TableCell className="border-bottom-0">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <MediumTypography
                        className="pr-sm"
                        label={
                          activity.otherAssocWorkHrsDec === null
                            ? '-'
                            : formatNumberToDecimal(
                                activity.otherAssocWorkHrsDec,
                              )
                        }
                      />
                      {activity.payrollHistoryFields?.includes(
                        'otherAssocWorkHrs',
                      ) && (
                        <HistoryIcon
                          className="enablePopupIcon"
                          onClick={() =>
                            setHistoryDialog({
                              open: true,
                              date: getAppStartTime(
                                activity.day,
                                'MM/DD/YYYY',
                                true,
                              ),
                              field: 'otherAssocWorkHrs',
                              employeeId: activityDetails?.employeeId ?? '',
                              titleId: 'otherAssocWorkHrsText',
                              defaultMessage: 'Other Assoc. Work Hrs',
                            })
                          }
                        />
                      )}
                    </Box>
                  </TableCell>
                )}

                <TableCell className="border-bottom-0 paddingZero">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <MediumTypography
                      className="pl-md pr-sm"
                      label={
                        activity.approvedNonBillableHoursDec === null
                          ? '-'
                          : formatNumberToDecimal(
                              activity.approvedNonBillableHoursDec,
                            )
                      }
                    />
                    {activity.payrollHistoryFields?.includes(
                      'approved_non_billable_hours',
                    ) && (
                      <HistoryIcon
                        className="enablePopupIcon"
                        onClick={() =>
                          setHistoryDialog({
                            open: true,
                            date: getAppStartTime(
                              activity.day,
                              'MM/DD/YYYY',
                              true,
                            ),
                            field: 'approved_non_billable_hours',
                            employeeId: activityDetails?.employeeId ?? '',
                            titleId: 'approvedNonBillableHoursText',
                            defaultMessage: 'Approved Non-Billable Hrs',
                          })
                        }
                      />
                    )}
                  </Box>
                </TableCell>

                {activityDetails?.employeeType === 'Hourly Non-Exempt' && (
                  <TableCell className="border-bottom-0 paddingZero">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <MediumTypography
                        className="pl-md pr-sm"
                        label={
                          activity.noShowTravelDocDec === null
                            ? '-'
                            : formatNumberToDecimal(activity.noShowTravelDocDec)
                        }
                      />
                      {activity.payrollHistoryFields?.includes(
                        'no_show_hours',
                      ) && (
                        <HistoryIcon
                          className="enablePopupIcon"
                          onClick={() =>
                            setHistoryDialog({
                              open: true,
                              date: getAppStartTime(
                                activity.day,
                                'MM/DD/YYYY',
                                true,
                              ),
                              field: 'no_show_hours',
                              employeeId: activityDetails?.employeeId ?? '',
                              titleId: 'noShowDescText',
                              defaultMessage:
                                'No Show (Travel, Wait, Doc, Other)',
                            })
                          }
                        />
                      )}
                    </Box>
                  </TableCell>
                )}

                <TableCell className="border-bottom-0 paddingZero">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <MediumTypography
                      className="pl-md pr-sm"
                      label={
                        activity.otherNonBillableHrsDec === null
                          ? '-'
                          : formatNumberToDecimal(
                              activity.otherNonBillableHrsDec,
                            )
                      }
                    />
                    {activity.payrollHistoryFields?.includes(
                      'other_non_billable_hours',
                    ) && (
                      <HistoryIcon
                        className="enablePopupIcon"
                        onClick={() =>
                          setHistoryDialog({
                            open: true,
                            date: getAppStartTime(
                              activity.day,
                              'MM/DD/YYYY',
                              true,
                            ),
                            field: 'other_non_billable_hours',
                            employeeId: activityDetails?.employeeId ?? '',
                            titleId: 'otherNonBillableHoursText',
                            defaultMessage: 'Other Non-Billable Hrs',
                          })
                        }
                      />
                    )}
                  </Box>
                </TableCell>

                <TableCell className="border-bottom-0">
                  <Box display={'flex'} alignItems={'center'}>
                    <MediumTypography
                      className="pr-xs"
                      label={
                        activity.hoursOutDec === null
                          ? '-'
                          : formatNumberToDecimalString(activity.hoursOutDec)
                      }
                    />
                    {activity.payrollHistoryFields?.includes('hours_out') && (
                      <HistoryIcon
                        className="enablePopupIcon"
                        onClick={() =>
                          setHoursOutHistoryDialog({
                            open: true,
                            employeeId: activityDetails?.employeeId ?? '',
                            date: getAppStartTime(
                              activity.day,
                              'MM/DD/YYYY',
                              true,
                            ),
                          })
                        }
                      />
                    )}
                    {displayPopup(activity) && (
                      <HoursOutPopover activity={activity} />
                    )}
                  </Box>
                </TableCell>

                {activityDetails?.employeeType !== 'Salaried Exempt' && (
                  <TableCell className="border-bottom-0 paddingZero">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <MediumTypography
                        className="pl-md pr-sm"
                        label={activity.lunchTimeIncluded ? 'Yes' : 'No'}
                      />
                      {activity.payrollHistoryFields?.includes(
                        'lunch_time_hours',
                      ) && (
                        <HistoryIcon
                          className="enablePopupIcon"
                          onClick={() =>
                            setHistoryDialog({
                              open: true,
                              date: getAppStartTime(
                                activity.day,
                                'MM/DD/YYYY',
                                true,
                              ),
                              field: 'lunch_time_hours',
                              employeeId: activityDetails?.employeeId ?? '',
                              titleId: 'lunchText',
                              defaultMessage: 'Lunch',
                            })
                          }
                        />
                      )}
                    </Box>
                  </TableCell>
                )}

                <TableCell className="border-bottom-0 paddingZero">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <MediumTypography
                      className="pl-md pr-sm"
                      label={
                        activity.totalHoursDec === null
                          ? '-'
                          : formatNumberToDecimal(activity.totalHoursDec)
                      }
                    />
                    {activity.payrollHistoryFields?.includes('total_hours') && (
                      <HistoryIcon
                        className="enablePopupIcon"
                        onClick={() =>
                          setHistoryDialog({
                            open: true,
                            date: getAppStartTime(
                              activity.day,
                              'MM/DD/YYYY',
                              true,
                            ),
                            field: 'total_hours',
                            employeeId: activityDetails?.employeeId ?? '',
                            titleId: 'totalHrsText',
                            defaultMessage: 'Total Hrs',
                          })
                        }
                      />
                    )}
                  </Box>
                </TableCell>

                <TableCell className="border-bottom-0">
                  <Box className="flex__ alignItemCenter">
                    <StatusIcon
                      fill={getStatusColor(
                        activity.payrollStatus ?? 'Not Submitted',
                      )}
                    />
                    <MediumTypography
                      className="paddingZero archivedIconMinusRemoveSm ml-xs"
                      label={activity.payrollStatus ?? 'Not Submitted'}
                      defaultlabel="Status"
                      sxProps={{
                        color: getStatusColor(
                          activity.payrollStatus ?? 'Not Submitted',
                        ),
                        whiteSpace: 'nowrap',
                      }}
                    />
                  </Box>
                </TableCell>

                <TableCell
                  className="border-bottom-0"
                  sx={{
                    display: checkPermissionForFeature(
                      'backend.payroll_hours_ras',
                      'viewPermission',
                    )
                      ? null
                      : 'none',
                  }}
                >
                  {activity.archived ? (
                    <Box
                      display={'inline-block'}
                      sx={{
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        paddingLeft: '8px',
                        paddingRight: '4px',
                      }}
                    >
                      <TextWithIcon
                        LeftIcon={
                          activity.archivalStatus !== null &&
                          activity.archivalStatus !== 'Submitted' && (
                            <UndoIcon />
                          )
                        }
                        className="paddingZero archivedIconMinusRemoveSm"
                        labelId={'archivedText'}
                        defaultLabel="Archived"
                        sxProps={{
                          color: '#97A6A5',
                          textTransform: 'none',
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                      }}
                    >
                      <ActionToolTip
                        labelId="BlockTime.note"
                        defaultLabel="Note"
                      >
                        <Box
                          sx={{
                            padding: '4px',
                            display: 'flex',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            if (activityDetails?.activities) {
                              updateNoteState(
                                activityDetails.activities[index],
                              );
                            }
                          }}
                        >
                          <MessageIcon
                            stroke={
                              activity.description ? '#00C6B8' : '#97A6A5'
                            }
                          />
                        </Box>
                      </ActionToolTip>
                      {activity.payrollStatus &&
                        activity.payrollStatus !== 'Not Submitted' && (
                          <>
                            <ActionToolTip
                              labelId="editText"
                              defaultLabel="Edit"
                            >
                              <Box
                                sx={{
                                  padding: '4px',
                                  display: 'flex',
                                  cursor: 'pointer',
                                  marginLeft: '8px',
                                }}
                                onClick={() => updateEditState(activity, true)}
                              >
                                <EditIcon />
                              </Box>
                            </ActionToolTip>
                            <ActionToolTip
                              labelId="archiveText"
                              defaultLabel="Archive"
                            >
                              <Box
                                sx={{
                                  padding: '4px',
                                  display: 'none',
                                  cursor: 'pointer',
                                  marginLeft: '8px',
                                }}
                              >
                                <ArchiveIcon />
                              </Box>
                            </ActionToolTip>
                          </>
                        )}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {hasPendingAction() && (
        <Box className="mt-sm">
          <MediumTypography
            className="mb-lg"
            labelid="reviewActvivitySheetAttestationDesc"
            defaultlabel="This record was submitted with an attestation confirming that the information is accurate, complete, and true to the best of their knowledge at the time of submission"
          />

          {checkPermissionForFeature(
            'backend.payroll_hours_ras',
            'editPermission',
          ) && (
            <Box className="flex__ mb-xlg" justifyContent={'end'}>
              <Box
                className="ml-xs"
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <InfoIcon style={{ cursor: 'pointer' }} />
                <Popover
                  id="mouse-over-popover"
                  sx={{
                    pointerEvents: 'none',
                  }}
                  open={open}
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  onClose={handlePopoverClose}
                  disableRestoreFocus
                >
                  <MediumTypography
                    className="p-sm"
                    labelid="payrollSubmissionInfo"
                    defaultlabel="Once you click <b>Save & Submit</b>, the system will approve all payroll hours and transmit the data to ThomBigBooks (TBB)"
                  />
                </Popover>
              </Box>
              <ButtonComponent
                className="btn-primary btn-submit ml-md"
                variantType="contained"
                type="submit"
                labelId={'clientpage.submit'}
                defaultLabelId="Save & Submit"
                onClick={() => {
                  context?.submitPayrollActivities?.();
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default PayrollHoursView;
